import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoPersonalSectionResponse } from '@/lib/api';
import { flattenSectionTree } from '../lib/sectionTree';

const NO_PARENT = 'none';

interface CreatePersonalSectionDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: string;
    description?: string;
    parentPersonalSectionId?: string | null;
  }) => void;
  /** Existing personal sections, offered as parent options. */
  sections: TodoPersonalSectionResponse[];
}

/**
 * Add a private section to the personal "My Tasks" board. Mirrors the shared
 * list's CreateSectionDialog (name + description + parent section) so adding
 * sections feels the same in both views. Only you can see it.
 */
export function CreatePersonalSectionDialog({
  open,
  onClose,
  onCreate,
  sections,
}: CreatePersonalSectionDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string>(NO_PARENT);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setParentId(NO_PARENT);
    }
  }, [open]);

  // Parent options in tree order, with nesting depth for indenting. Personal
  // sections use their own id/parent field names, so adapt to the tree shape.
  const orderedParents = useMemo(
    () =>
      flattenSectionTree(
        sections.map((s) => ({
          sectionId: s.personalSectionId,
          parentSectionId: s.parentPersonalSectionId ?? null,
          sortOrder: s.sortOrder,
          name: s.name,
        })),
      ),
    [sections],
  );

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate({
      name: trimmed,
      description: description.trim() || undefined,
      parentPersonalSectionId: parentId !== NO_PARENT ? parentId : null,
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Section</DialogTitle>
          <DialogDescription>
            Add a private section to organize your own view. Only you can see it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="personal-section-name">Name</Label>
            <Input
              id="personal-section-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Today, This Week"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="personal-section-description">Description (optional)</Label>
            <Textarea
              id="personal-section-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What goes in this section?"
              rows={2}
            />
          </div>

          {sections.length > 0 && (
            <div className="space-y-2">
              <Label>Parent Section</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger>
                  <SelectValue placeholder="None (top-level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PARENT}>None (top-level)</SelectItem>
                  {orderedParents.map(({ section, depth }) => (
                    <SelectItem key={section.sectionId} value={section.sectionId}>
                      <span style={{ paddingLeft: `${depth}rem` }}>{section.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
